import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Loader2 } from "lucide-react";

const cannedReplyFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  bodyHtml: z.string().optional(),
  isShared: z.boolean().default(true),
});

export type CannedReplyFormValues = z.infer<typeof cannedReplyFormSchema>;

interface CannedReplyFormProps {
  defaultValues?: Partial<CannedReplyFormValues>;
  onSubmit: (data: CannedReplyFormValues) => void;
  isPending?: boolean;
}

export function CannedReplyForm({
  defaultValues,
  onSubmit,
  isPending,
}: CannedReplyFormProps) {
  const form = useForm<CannedReplyFormValues>({
    resolver: zodResolver(cannedReplyFormSchema),
    defaultValues: {
      title: "",
      bodyHtml: "",
      isShared: true,
      ...defaultValues,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title *</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Password Reset Instructions" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="bodyHtml"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Body (HTML)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="<p>Enter the reply template HTML...</p>"
                  className="min-h-[150px] font-mono text-sm"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="isShared"
          render={({ field }) => (
            <FormItem className="flex items-center gap-3 space-y-0">
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div>
                <FormLabel>Shared</FormLabel>
                <FormDescription>
                  Make this reply available to all team members
                </FormDescription>
              </div>
            </FormItem>
          )}
        />

        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {defaultValues?.title ? "Update Reply" : "Create Reply"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
